import React, { useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Button,
  Typography,
  ListItemText,
  IconButton,
  Box,
  Chip,
  OutlinedInput,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../axios/axios";
import { END_POINTS } from "../../shared/endpoints";
import { toast } from "react-toastify";
import RescanWithCredentialPopup from "../onPremises/assetsManagement/RescanPopup";
import {
  canMapTemplateToAsset,
  MapAssetTemplateDevice,
} from "../onPremises/assetsManagement/MapAssetTemplateDialog";

interface Credential {
  id: string;
  name: string;
  type?: string;
}

interface AgentOption {
  id: string;
  name: string;
  agent_type?: "cloud" | "on_prem";
  status_display?: string;
}

type ScanTypeId = "onprem-discovery" | "onprem-discovery-posture" | "onprem-posture";

interface DiscoveryRescanPopupProps {
  open: boolean;
  onClose: () => void;
  rowData: any;
  onRescanSuccess?: (resData: any) => void;
  initialAgents?: AgentOption[];
  useNewScanCredentialApi?: boolean;
  defaultScanType?: ScanTypeId;
  disableScanType?: boolean;
  hideCreateCredential?: boolean;
  title?: string;
}

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    height: "48px",
  },
};

const selectSx = {
  borderRadius: "8px",
  height: "48px",
};

const DiscoveryRescanPopup: React.FC<DiscoveryRescanPopupProps> = ({
  open,
  onClose,
  rowData,
  onRescanSuccess,
  initialAgents,
  useNewScanCredentialApi = false,
  defaultScanType,
  disableScanType = false,
  hideCreateCredential = false,
  title = "Rescan Asset Inventory",
}) => {
  const navigate = useNavigate();

  /* ── Core form state ── */
  const [scanName, setScanName] = useState("");
  const [description, setDescription] = useState("");
  const [scanType, setScanType] = useState<ScanTypeId>(defaultScanType || "onprem-discovery");

  /* ── Agent ── */
  const [allAgents, setAllAgents] = useState<AgentOption[]>([]);
  const [agentId, setAgentId] = useState("");
  const [isAgentsLoading, setIsAgentsLoading] = useState(false);

  /* ── CIDR ── */
  const [cidrInput, setCidrInput] = useState("");
  const [cidrBlocks, setCidrBlocks] = useState<string[]>([]);
  const [cidrError, setCidrError] = useState("");
  const [isValidatingIp, setIsValidatingIp] = useState(false);
  const cidrInputRef = useRef<HTMLInputElement | null>(null);

  /* ── Chunk / Interval ── */
  const [chunk, setChunk] = useState("");
  const [chunkError, setChunkError] = useState("");
  const [interval, setInterval] = useState("");
  const [intervalError, setIntervalError] = useState("");

  /* ── Multi-select credentials ── */
  const [sshCredentials, setSshCredentials] = useState<Credential[]>([]);
  const [selectedCredentials, setSelectedCredentials] = useState<string[]>([]);
  const [isCredentialsLoading, setIsCredentialsLoading] = useState(false);

  /* ── Config template (on-prem network assets) ── */
  type TemplateOption = {
    template_id: string;
    name: string;
    vendor_name: string;
    device_model_name: string;
  };
  const [templateOptions, setTemplateOptions] = useState<TemplateOption[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [isTemplatesLoading, setIsTemplatesLoading] = useState(false);

  const norm = (v: any) => String(v ?? "").trim().toLowerCase();
  const getVendor = (d: any) =>
    String(d?.vendor || d?.vendor_name || "").trim();
  const getModel = (d: any) =>
    String(d?.model || d?.device_model_name || d?.os || "").trim();

  const rowAssets: MapAssetTemplateDevice[] = Array.isArray(rowData)
    ? rowData
    : rowData
      ? [rowData]
      : [];

  const templateEligibleAssets = rowAssets.filter((d) =>
    canMapTemplateToAsset(d)
  );

  const canShowTemplatePicker = (() => {
    if (templateEligibleAssets.length === 0) return false;
    const vendors = new Set(
      templateEligibleAssets.map((d) => norm(getVendor(d)))
    );
    const models = new Set(
      templateEligibleAssets.map((d) => norm(getModel(d)))
    );
    return vendors.size === 1 && models.size === 1;
  })();

  /* ── Create New Credential Popup state ── */
  const [isRescanWithCredOpen, setIsRescanWithCredOpen] = useState(false);
  const [assetsCredData, setAssetsCredData] = useState<any[]>([]);
  const [credTypesData, setCredTypesData] = useState<any[]>([]);

  const handleOpenCreateCredPopup = async () => {
    if (!agentId) {
      toast.error("Please select an Agent first");
      return;
    }
    setIsRescanWithCredOpen(true);
    try {
      const resCreds = await axiosInstance.get(END_POINTS.CREDENTIAL_MGMT);
      if (resCreds?.status === 200) {
        setAssetsCredData(resCreds?.data?.data?.cred_list || []);
      }
    } catch (err) {
      console.error("Error fetching credentials:", err);
    }
    try {
      const resTypes = await axiosInstance.get(END_POINTS.CREDENTIAL_TYPES);
      if (resTypes?.status === 200) {
        setCredTypesData(resTypes?.data?.data || []);
      }
    } catch (err) {
      console.error("Error fetching credential types:", err);
    }
  };

  /* ── Submit ── */
  const [isConnecting, setIsConnecting] = useState(false);

  /* ── Effects ── */
  useEffect(() => {
    if (open) {
      // Pre-fill IP from row (checking ip_range, host, ip, ip_address, ipAddress)
      let ips: string[] = [];
      if (Array.isArray(rowData)) {
        ips = rowData
          .flatMap((r: any) => {
            if (Array.isArray(r?.ip_range) && r.ip_range.length > 0) {
              return r.ip_range.filter(Boolean);
            }
            if (typeof r?.ip_range === "string" && r.ip_range.trim()) {
              return r.ip_range.split(/[\n,]/).map((s: string) => s.trim()).filter(Boolean);
            }
            const single = r?.host || r?.ip || r?.ip_address || r?.ipAddress;
            return single ? [String(single).trim()] : [];
          })
          .filter(Boolean);
      } else if (rowData) {
        if (Array.isArray(rowData?.ip_range) && rowData.ip_range.length > 0) {
          ips = rowData.ip_range.filter(Boolean);
        } else if (typeof rowData?.ip_range === "string" && rowData.ip_range.trim()) {
          ips = rowData.ip_range.split(/[\n,]/).map((s: string) => s.trim()).filter(Boolean);
        } else {
          const single = rowData?.host || rowData?.ip || rowData?.ip_address || rowData?.ipAddress || "";
          if (single) {
            ips = [String(single).trim()];
          }
        }
      }
      setScanName(rowData?.scanName || rowData?.name || rowData?.scan_name || "");
      setCidrBlocks(ips);

      // Pre-fill Agent if available
      const prefilledAgentId = rowData?.agent_id || rowData?.agentId;
      if (prefilledAgentId) {
        setAgentId(String(prefilledAgentId));
      }

      if (initialAgents && initialAgents.length > 0) {
        setAllAgents(initialAgents);
        if (!prefilledAgentId && rowData?.agent && typeof rowData.agent === "string") {
          const agentStr = rowData.agent.trim().toLowerCase();
          const match = initialAgents.find(
            (a) => a.name?.toLowerCase() === agentStr || String(a.id) === agentStr
          );
          if (match) setAgentId(String(match.id));
        }
      } else {
        fetchAgents();
      }
      fetchCredentials();

      if (defaultScanType) {
        setScanType(defaultScanType);
      } else if (rowData && !Array.isArray(rowData)) {
        const rawType = String(rowData?.type || rowData?.scan_type || "").toLowerCase();
        if (rawType.includes("discovery") && rawType.includes("posture")) {
          setScanType("onprem-discovery-posture");
        } else if (rawType.includes("discovery")) {
          setScanType("onprem-discovery");
        } else if (rawType.includes("posture") || rawType === "onprem" || rawType === "on_prem") {
          // Asset Inventory on-prem network devices → posture rescan path
          setScanType("onprem-posture");
        } else {
          setScanType("onprem-discovery");
        }
      } else if (Array.isArray(rowData) && rowData.length > 0) {
        const rawType = String(
          rowData[0]?.type || rowData[0]?.scan_type || ""
        ).toLowerCase();
        if (
          !rawType.includes("discovery") &&
          (rawType.includes("posture") ||
            rawType === "onprem" ||
            rawType === "on_prem" ||
            canMapTemplateToAsset(rowData[0]))
        ) {
          setScanType("onprem-posture");
        }
      }

      setSelectedTemplateId("");
      setTemplateOptions([]);
    } else {
      resetForm();
    }
  }, [open, rowData, initialAgents, defaultScanType]);

  const resetForm = () => {
    setScanName("");
    setDescription("");
    setScanType(defaultScanType || "onprem-discovery");
    setAgentId("");
    setCidrInput("");
    setCidrBlocks([]);
    setCidrError("");
    setChunk("");
    setChunkError("");
    setInterval("");
    setIntervalError("");
    setSelectedCredentials([]);
    setSelectedTemplateId("");
    setTemplateOptions([]);
  };

  /* Load config templates for eligible on-prem network assets */
  useEffect(() => {
    if (!open || !canShowTemplatePicker) {
      setTemplateOptions([]);
      setSelectedTemplateId("");
      return;
    }

    const primary = templateEligibleAssets[0];
    const vendorFilter = norm(getVendor(primary));
    const modelFilter = norm(getModel(primary));
    const deviceIds = templateEligibleAssets
      .map((d) => String(d.id || ""))
      .filter(Boolean);

    const isTemplateActive = (row: any): boolean => {
      const value = row?.active ?? row?.is_active;
      if (typeof value === "boolean") return value;
      if (typeof value === "number") return value === 1;
      if (typeof value === "string") {
        const n = value.trim().toLowerCase();
        return n === "true" || n === "active" || n === "1" || n === "yes";
      }
      return Boolean(value);
    };
    const isTemplateSuccess = (row: any): boolean => {
      const label = String(row?.status_label ?? "").trim().toLowerCase();
      if (label === "success") return true;
      return row?.status === 3 || row?.status === "3";
    };

    const load = async () => {
      setIsTemplatesLoading(true);
      try {
        const [templatesRes, mappingsRes] = await Promise.all([
          axiosInstance.get(END_POINTS.MASTER_TEMPLATE_LIST, {
            params: { active: true },
          }),
          axiosInstance.get(END_POINTS.MASTER_TEMPLATE_DEVICE_MAPPING, {
            params: { active: true },
          }),
        ]);

        const rawTemplates =
          templatesRes?.data?.data?.templates ||
          templatesRes?.data?.data?.results ||
          templatesRes?.data?.data ||
          templatesRes?.data ||
          [];
        const templateList = Array.isArray(rawTemplates) ? rawTemplates : [];
        const options: TemplateOption[] = templateList
          .filter((t: any) => isTemplateActive(t) && isTemplateSuccess(t))
          .filter((t: any) => {
            if (vendorFilter && norm(t.vendor_name) !== vendorFilter) return false;
            if (modelFilter && norm(t.device_model_name) !== modelFilter)
              return false;
            return true;
          })
          .map((t: any) => ({
            template_id: String(t.template_id || t.id || ""),
            name: String(t.name || t.template_name || t.file_name || "Template"),
            vendor_name: String(t.vendor_name || ""),
            device_model_name: String(t.device_model_name || ""),
          }))
          .filter((t: TemplateOption) => t.template_id);

        setTemplateOptions(options);

        // Pre-select only when a single device is already mapped to a valid option
        let preselect = "";
        if (deviceIds.length === 1) {
          const rawMappings =
            mappingsRes?.data?.data?.mappings ||
            mappingsRes?.data?.data?.results ||
            mappingsRes?.data?.data ||
            mappingsRes?.data ||
            [];
          const mappings = Array.isArray(rawMappings) ? rawMappings : [];
          const active = mappings.find(
            (m: any) =>
              String(m.scan_device_id || m.device_id || "") === deviceIds[0] &&
              m.active !== false
          );
          const mappedId = String(
            active?.template_id || active?.master_template_id || ""
          );
          if (mappedId && options.some((o) => o.template_id === mappedId)) {
            preselect = mappedId;
          }
        }
        setSelectedTemplateId(preselect);
      } catch (err) {
        console.error("Error fetching config templates for rescan:", err);
        setTemplateOptions([]);
        setSelectedTemplateId("");
      } finally {
        setIsTemplatesLoading(false);
      }
    };

    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, canShowTemplatePicker, rowData]);

  /* ── API calls ── */
  const fetchAgents = async () => {
    setIsAgentsLoading(true);
    try {
      const res = await axiosInstance.get(END_POINTS.CLOUD_AGENT);
      const data = res?.data?.data || res?.data || [];
      const list: AgentOption[] = Array.isArray(data) ? data : data.agent_list || [];
      setAllAgents(list);

      // Try matching agent if not already selected
      const prefilledAgentId = rowData?.agent_id || rowData?.agentId;
      if (prefilledAgentId) {
        setAgentId(String(prefilledAgentId));
      } else if (rowData?.agent && typeof rowData.agent === "string") {
        const agentStr = rowData.agent.trim().toLowerCase();
        const match = list.find(
          (a) => a.name?.toLowerCase() === agentStr || String(a.id) === agentStr
        );
        if (match) setAgentId(String(match.id));
      }
    } catch (err) {
      console.error("Error fetching agents:", err);
    } finally {
      setIsAgentsLoading(false);
    }
  };

  const fetchCredentials = async () => {
    setIsCredentialsLoading(true);
    try {
      const res = await axiosInstance.get(END_POINTS.CREDENTIAL_MGMT);
      const list = res?.data?.data?.cred_list || res?.data?.data || res?.data || [];
      const credList = Array.isArray(list) ? list : [];
      setSshCredentials(credList);

      // Pre-select credentials if rowData has cred_ids or cred_id
      const prefilledCreds = rowData?.cred_ids || rowData?.cred_id;
      if (prefilledCreds) {
        if (Array.isArray(prefilledCreds)) {
          const ids = prefilledCreds.map(String).filter(Boolean);
          if (ids.length > 0) setSelectedCredentials(ids);
        } else if (String(prefilledCreds).trim()) {
          setSelectedCredentials([String(prefilledCreds).trim()]);
        }
      }
    } catch (err) {
      console.error("Error fetching credentials:", err);
    } finally {
      setIsCredentialsLoading(false);
    }
  };

  const handleValidateIp = async (isAuto = false) => {
    const combinedIpRanges = [
      ...cidrBlocks,
      ...cidrInput.split(/[\n,]/).map((s) => s.trim()).filter(Boolean),
    ];

    if (combinedIpRanges.length === 0) {
      if (!isAuto) {
        toast.error("Please enter at least one IP range or CIDR block.");
      }
      return false;
    }

    setIsValidatingIp(true);
    setCidrError("");
    try {
      const response = await axiosInstance.post(END_POINTS.VALIDATE_IP, {
        ip_range: combinedIpRanges,
      });

      if (response.status === 200 || response.status === 201) {
        const results = response.data?.data?.results || [];
        const invalidResult = results.find((r: any) => r.is_valid === false);

        if (invalidResult) {
          setCidrError(invalidResult.error || "Invalid CIDR format");
          return false;
        }

        if (!isAuto) {
          toast.success(response.data?.message || "IP validation successful");
        }
        setCidrError("");
        return true;
      }
      return false;
    } catch (error: any) {
      console.error("Error validating IP:", error);
      const errorData = error?.response?.data;
      if (error?.response?.status === 400) {
        setCidrError(errorData?.message || "Invalid IP address or CIDR range");
      } else if (!isAuto) {
        toast.error(errorData?.message || "Invalid IP address or CIDR range");
      }
      return false;
    } finally {
      setIsValidatingIp(false);
    }
  };

  /* ── CIDR helpers ── */
  const addCidrFromInput = () => {
    const raw = cidrInput.trim();
    if (!raw) return;

    const parts = raw
      .split(/[\n,]/)
      .map((p) => p.trim())
      .filter(Boolean);

    if (!parts.length) return;

    setCidrBlocks((prev) => {
      const next = [...prev];
      parts.forEach((p) => {
        if (!next.includes(p)) next.push(p);
      });
      return next;
    });

    setCidrInput("");
    setCidrError("");
  };

  const handleCidrKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault();
      addCidrFromInput();
    } else if (
      (event.key === "Backspace" || event.key === "Delete") &&
      !cidrInput &&
      cidrBlocks.length
    ) {
      setCidrBlocks((prev) => prev.slice(0, -1));
      setCidrError("");
    }
  };

  const handleRemoveCidr = (block: string) => {
    setCidrBlocks((prev) => prev.filter((b) => b !== block));
    setCidrError("");
  };

  /* ── Submit ── */
  const handleConnect = async () => {
    setIsConnecting(true);
    try {
      if (scanType !== "onprem-discovery") {
        let assetIdList: string[] = [];
        if (Array.isArray(rowData)) {
          assetIdList = rowData.map((r: any) => r?.id || r?.device_id).filter(Boolean);
        } else if (rowData) {
          const id = typeof rowData === "object" ? (rowData?.id || rowData?.device_id || rowData?.onprem_posture_id) : String(rowData);
          if (id) assetIdList = [id];
        }

        const credId = Array.isArray(selectedCredentials)
          ? selectedCredentials[0] || ""
          : selectedCredentials || "";

        const scanIdVal = rowData?.scan_uuid || rowData?.scanId || rowData?.scan_id;
        const posturePayload: Record<string, any> = {
          asset_id: assetIdList,
          scan_source: rowData?.scan_source || (scanIdVal ? "Scans" : "Assets"),
          agent_id: agentId,
          cred_id: credId,
        };

        if (scanIdVal) {
          posturePayload.scan_id = scanIdVal;
        }

        if (selectedTemplateId && canShowTemplatePicker) {
          posturePayload.template_id = selectedTemplateId;
        }

        console.log("On-Prem Posture / Discovery + Posture Rescan payload:", posturePayload);

        const response = await axiosInstance.post(END_POINTS.RESCAN, posturePayload);
        if (response?.status === 200 || response?.status === 201) {
          toast.success("Rescan submitted successfully");
          if (typeof onRescanSuccess === "function") {
            onRescanSuccess(response.data);
          }
          onClose();
        }
        return;
      }

      const combinedIpRanges = [
        ...cidrBlocks,
        ...cidrInput.split(/[\n,]/).map((s) => s.trim()).filter(Boolean),
      ];

      if (combinedIpRanges.length === 0) {
        setCidrError("Please enter at least one IP range or CIDR block.");
        setIsConnecting(false);
        return;
      }

      // Auto-validate before submit
      const isValid = await handleValidateIp(true);
      if (!isValid) {
        setIsConnecting(false);
        return;
      }

      const scanTypeMap: Record<string, string> = {
        "onprem-discovery": "onprem_discovery",
        "onprem-discovery-posture": "onprem_discovery_posture",
        "onprem-posture": "onprem_posture",
      };

      const payload: Record<string, any> = {
        scan_type: scanTypeMap[scanType] || scanType,
        agent_id: agentId,
        scanname: scanName.trim() || (rowData?.scanName || rowData?.name || rowData?.scan_name || "Rescan"),
        description: description.trim(),
        ip_range: combinedIpRanges,
        cred_ids: selectedCredentials,
        schedule: false,
        new_cred: false,
        save: false,
      };

      if (scanType === "onprem-discovery" || scanType === "onprem-discovery-posture") {
        payload.interval = interval || "1";
        payload.chunk_size = chunk || "16";
      }

      if (rowData && !Array.isArray(rowData)) {
        const assetId = rowData?.id || rowData?.device_id || rowData?.scan_uuid || rowData?.scanId || rowData?.scan_id;
        if (assetId) {
          payload.asset_id = assetId;
        }
      }

      console.log("Rescan payload:", payload);

      const response = await axiosInstance.post(END_POINTS.NEW_SCAN_TYPES, payload);
      if (response?.status === 200 || response?.status === 201) {
        toast.success("Rescan submitted successfully");
        if (typeof onRescanSuccess === "function") {
          onRescanSuccess(response.data);
        }
        onClose();
      }
    } catch (error: any) {
      console.error("Error submitting rescan:", error);
      toast.error(error?.response?.data?.message || "Failed to submit rescan");
    } finally {
      setIsConnecting(false);
    }
  };

  /* ── Derived ── */
  const filteredAgents = allAgents.filter(
    (agent) => String(agent.status_display || "").trim().toLowerCase() === "active"
  );

  const isCidrMissing = cidrBlocks.length === 0 && !cidrInput.trim();
  const isConnectDisabled =
    scanType === "onprem-posture"
      ? !agentId || selectedCredentials.length === 0 || isConnecting
      : isCidrMissing || !agentId || isConnecting;

  /* ── Render ── */
  // Hide the inventory rescan dialog while Create New Credential is open so
  // the two dialogs are not stacked (parent visible behind the create popup).
  const showRescanDialog = open && !isRescanWithCredOpen;

  return (
    <>
    <Dialog 
      open={showRescanDialog} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      sx={{ 
        "& .MuiDialog-container": { alignItems: "flex-start" }, 
        "& .MuiDialog-paper": { mt: 15 } 
      }}
    >
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        {/* <Typography variant="h6">Rescan Asset Inventory</Typography> */}
        <Typography variant="h6">{title}</Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box display="flex" flexDirection="column" gap={2} mt={1}>
          {/* Scan Name & Description */}
          {/* <Box display="flex" gap={2}>
            <TextField
              label="Scan Name"
              value={scanName}
              onChange={(e) => setScanName(e.target.value)}
              variant="outlined"
              size="small"
              fullWidth
              sx={fieldSx}
            />
            <TextField
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              variant="outlined"
              size="small"
              fullWidth
              sx={fieldSx}
            />
          </Box> */}

          {/* Scan Type & Agent */}
          <Box display="flex" gap={2}>
            <FormControl fullWidth size="small" disabled={disableScanType}>
              <InputLabel>Scan Type</InputLabel>
              <Select
                value={scanType}
                label="Scan Type"
                onChange={(e) => setScanType(e.target.value as ScanTypeId)}
                sx={selectSx}
              >
                <MenuItem value="onprem-discovery">On-Prem Discovery</MenuItem>
                <MenuItem value="onprem-discovery-posture">On-Prem Discovery + Posture</MenuItem>
                <MenuItem value="onprem-posture">On-Prem Posture</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <InputLabel>Agent</InputLabel>
              <Select
                value={agentId}
                label="Agent"
                onChange={(e) => setAgentId(e.target.value)}
                sx={selectSx}
                disabled={isAgentsLoading}
                MenuProps={{
                  PaperProps: { style: { maxHeight: 320 }, className: "scroll" },
                }}
              >
                {isAgentsLoading ? (
                  <MenuItem disabled>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Loading agents...
                  </MenuItem>
                ) : filteredAgents.length === 0 ? (
                  <MenuItem disabled>No active agents</MenuItem>
                ) : (
                  filteredAgents.map((agent) => (
                    <MenuItem key={agent.id} value={agent.id}>
                      {agent.name}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </Box>

          {/* CIDR Block */}
          <Box>
            {/* <Typography sx={{ mb: 0.5, fontSize: 13, fontWeight: 600 }}>
              Enter CIDR Block
            </Typography>
            <Typography sx={{ fontSize: 12, color: "#534F7A", fontWeight: 500, mb: 1 }}>
              Press Enter after each CIDR block to add multiple entries.
            </Typography> */}
            <Box
              sx={{
                border: "1px solid",
                borderColor: cidrError ? "error.main" : "grey.400",
                borderRadius: "8px",
                p: 1,
                display: "flex",
                flexWrap: "wrap",
                gap: 0.5,
                alignItems: "center",
                minHeight: 48
              }}
            >
              {cidrBlocks.map((block) => (
                <Chip
                  key={block}
                  label={block}
                  size="small"
                  // onDelete={() => handleRemoveCidr(block)}
                />
              ))}
              <input
                ref={cidrInputRef}
                value={cidrInput}
                disabled
                onChange={(e) => {
                  const val = e.target.value;
                  if (/^[0-9./, ]*$/.test(val)) {
                    setCidrInput(val);
                  }
                }}
                onKeyDown={handleCidrKeyDown}
                placeholder={
                  cidrBlocks.length === 0 && !cidrInput.trim()
                    ? "e.g. 192.168.1.0/24"
                    : ""
                }
                style={{
                  border: "none",
                  outline: "none",
                  flex: 1,
                  minWidth: 120,
                  fontSize: 14,
                  background: "transparent",
                  color: "inherit",
                }}
              />
            </Box>
            <Box display="flex" alignItems="center" gap={1} mt={1}>
              {/* <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={() => handleValidateIp(false)}
                disabled={isValidatingIp || (cidrBlocks.length === 0 && !cidrInput.trim())}
                startIcon={isValidatingIp ? <CircularProgress size={16} color="inherit" /> : null}
              >
                {isValidatingIp ? "Validating..." : "Validate"}
              </Button> */}
            </Box>
            {cidrError && (
              <Typography sx={{ fontSize: 13, fontWeight: 500, color: "error.main", mt: 0.5 }}>
                {cidrError}
              </Typography>
            )}
          </Box>

          {/* Chunk Size & Interval */}
          {/* {(cidrBlocks.length > 0 || cidrInput.trim()) && (
            <Box display="flex" gap={2}>
              <Box flex={1}>
                <TextField
                  label="Chunk Size"
                  value={chunk}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (/^\d*$/.test(val)) {
                      setChunk(val);
                      if (val && parseInt(val, 10) < 1) {
                        setChunkError("Minimum 1 chunk size is required");
                      } else {
                        setChunkError("");
                      }
                    }
                  }}
                  variant="outlined"
                  size="small"
                  fullWidth
                  sx={fieldSx}
                  error={!!chunkError}
                />
                {chunkError && (
                  <Typography color="error" sx={{ mt: 0.5, fontSize: 11, fontWeight: 500 }}>
                    {chunkError}
                  </Typography>
                )}
              </Box>
              <Box flex={1}>
                <TextField
                  label="Interval in minutes"
                  value={interval}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (/^\d*$/.test(val)) {
                      setInterval(val);
                      if (val && parseInt(val, 10) < 1) {
                        setIntervalError("Minimum interval required is 1 minute");
                      } else {
                        setIntervalError("");
                      }
                    }
                  }}
                  variant="outlined"
                  size="small"
                  fullWidth
                  sx={fieldSx}
                  error={!!intervalError}
                />
                {intervalError && (
                  <Typography color="error" sx={{ mt: 0.5, fontSize: 11, fontWeight: 500 }}>
                    {intervalError}
                  </Typography>
                )}
              </Box>
            </Box>
          )} */}

          {/* Config template — on-prem network assets (optional) */}
          {canShowTemplatePicker && (
            <Box>
              <Typography
                sx={{ mb: 0.75, fontSize: 13, fontWeight: 600, color: "text.secondary" }}
              >
                Config Template
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  id="rescan-select-template"
                  displayEmpty
                  value={selectedTemplateId}
                  onChange={(e) =>
                    setSelectedTemplateId(String(e.target.value))
                  }
                  disabled={isTemplatesLoading}
                  sx={selectSx}
                  renderValue={(value) => {
                    if (!value) {
                      return (
                        <Typography
                          sx={{ fontSize: 14, color: "text.secondary" }}
                        >
                          Select template
                        </Typography>
                      );
                    }
                    const match = templateOptions.find(
                      (t) => t.template_id === value
                    );
                    return match?.name || String(value);
                  }}
                  MenuProps={{
                    PaperProps: {
                      style: { maxHeight: 280 },
                      className: "scroll",
                    },
                  }}
                >
                  <MenuItem value="">
                    <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                      Select template
                    </Typography>
                  </MenuItem>
                  {isTemplatesLoading ? (
                    <MenuItem disabled>
                      <CircularProgress size={18} sx={{ mr: 1 }} />
                      Loading templates...
                    </MenuItem>
                  ) : templateOptions.length === 0 ? (
                    <MenuItem disabled>
                      No matching Success templates
                    </MenuItem>
                  ) : (
                    templateOptions.map((t) => (
                      <MenuItem key={t.template_id} value={t.template_id}>
                        {t.name}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Box>
          )}

          {/* Multi-select Credentials */}
          <FormControl fullWidth>
            <InputLabel id="rescan-select-credentials-label">Select Credentials</InputLabel>
            <Select
              labelId="rescan-select-credentials-label"
              id="rescan-select-credentials"
              multiple
              value={selectedCredentials}
              onChange={(e) => {
                const value = e.target.value;
                const selected = typeof value === "string" ? value.split(",") : value;
                if (selected.includes("create_new")) {
                  handleOpenCreateCredPopup();
                  return;
                }
                setSelectedCredentials(selected.filter((v: string) => v !== "create_new" && v !== "all"));
              }}
              input={<OutlinedInput label="Select Credentials" />}
              renderValue={(selected) => (
                <Box 
                  id="discovery-creds-box"
                  className="scroll"
                  sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignContent: "flex-start",
                  gap: 0.5,
                  maxHeight: "56px",
                  overflowY: "auto",
                  pb: 0.5,
                  pr: 0.5
                }}>
                  {selected.map((credId) => {
                    const cred = sshCredentials.find((c) => String(c.id) === String(credId));
                    return (
                      <Chip
                        key={credId}
                        label={cred?.name || credId}
                        size="small"
                        onDelete={() =>
                          setSelectedCredentials((prev) =>
                            prev.filter((id) => id !== credId)
                          )
                        }
                        onMouseDown={(e) => e.stopPropagation()}
                      />
                    );
                  })}
                </Box>
              )}
              sx={{ ...selectSx, height: "auto", minHeight: "48px" }}
              MenuProps={{
                sx: { mt: 1 },
                variant: "menu",
                anchorOrigin: {
                  vertical: "bottom",
                  horizontal: "left",
                },
                transformOrigin: {
                  vertical: "top",
                  horizontal: "left",
                },
                PaperProps: { style: { maxHeight: 250, marginTop: 8 }, className: "scroll" },
                slotProps: {
                  backdrop: {
                    onWheel: (e: any) => {
                      const box = document.getElementById("discovery-creds-box");
                      if (box) {
                        const rect = box.getBoundingClientRect();
                        if (
                          e.clientX >= rect.left && e.clientX <= rect.right &&
                          e.clientY >= rect.top && e.clientY <= rect.bottom
                        ) {
                          box.scrollTop += e.deltaY;
                        }
                      }
                    }
                  } as any
                }
              }}
            >
              {!hideCreateCredential && (
                <MenuItem
                  key="create-new-cred"
                  value="create_new"
                  sx={{
                    color: "#0F02C3",
                    fontWeight: 600,
                    fontSize: 13,
                    py: 1,
                    display: "flex",
                    alignItems: "center",
                    borderBottom: "1px solid #e2e8f0",
                    "&:hover": { backgroundColor: "#f0f4ff" },
                  }}
                >
                  <AddCircleOutlineIcon size="small" sx={{ mr: 1, fontSize: 18, color: "#0F02C3" }} />
                  Create New Credential
                </MenuItem>
              )}
              {isCredentialsLoading ? (
                <MenuItem disabled>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Loading credentials...
                </MenuItem>
              ) : sshCredentials.length === 0 ? (
                <MenuItem disabled>No credentials found</MenuItem>
              ) : (
                sshCredentials.map((cred) => (
                  <MenuItem key={cred.id} value={cred.id}>
                    <Checkbox checked={selectedCredentials.includes(cred.id)} size="small" />
                    <ListItemText primary={cred.name} />
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleConnect}
          disabled={isConnectDisabled}
          variant="contained"
          color="primary"
        >
          {isConnecting ? "Submitting..." : "Rescan"}
        </Button>
      </DialogActions>

    </Dialog>

      <RescanWithCredentialPopup
        open={isRescanWithCredOpen}
        defaultCreateNew={true}
        useNewScanApi={useNewScanCredentialApi && scanType === "onprem-discovery"}
        newScanContext={{
          scanType: "onprem_discovery",
          ipRange: [...cidrBlocks, ...cidrInput.split(/[\n,]/).map((s) => s.trim()).filter(Boolean)],
        }}
        agentId={agentId}
        onClose={() => {
          setIsRescanWithCredOpen(false);
          fetchCredentials();
        }}
        assetsCredData={assetsCredData}
        credData={credTypesData}
        deviceId={
          Array.isArray(rowData)
            ? rowData.map((r: any) => r?.id || r?.device_id).filter(Boolean)
            : [rowData?.id || rowData?.device_id].filter(Boolean)
        }
        rescanCallback={(resData: any) => {
          setIsRescanWithCredOpen(false);
          fetchCredentials();
          if (typeof onRescanSuccess === "function") {
            onRescanSuccess(resData);
          }
          onClose();
        }}
      />
    </>
  );
};

export default DiscoveryRescanPopup;
